import { apiClient } from '@neighbourhood-munchies/api-client'
import { MenuCustomisation, ObjectId } from '@neighbourhood-munchies/shared-types'
import { useQueries, useQuery } from '@tanstack/react-query'

/**
 * Hook to fetch customizations for a single menu item
 * Uses React Query for caching and deduplication
 */
export const useMenuItemCustomizations = (menuItemId: ObjectId | null) => {
  return useQuery({
    queryKey: ['menu-customizations', menuItemId],
    queryFn: async () => {
      if (!menuItemId) return []

      const response = await apiClient.menuItems.getMenuCustomisations(menuItemId)
      return response.success ? response.data || [] : []
    },
    enabled: !!menuItemId,
    staleTime: 15 * 60 * 1000, // 15 minutes - customizations rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  })
}

/**
 * Hook to fetch customizations for multiple menu items in parallel
 * Automatically deduplicates requests and uses cached data
 */
export const useMultipleMenuItemCustomizations = (menuItemIds: ObjectId[]) => {
  const queries = useQueries({
    queries: menuItemIds.map((menuItemId) => ({
      queryKey: ['menu-customizations', menuItemId],
      queryFn: async () => {
        const response = await apiClient.menuItems.getMenuCustomisations(menuItemId)
        return response.success ? response.data || [] : []
      },
      staleTime: 15 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000),
    })),
  })

  // Transform results into a map for easy lookup
  const customizationsMap = new Map<ObjectId, MenuCustomisation[]>()
  queries.forEach((query, index) => {
    if (query.data) {
      customizationsMap.set(menuItemIds[index], query.data)
    }
  })

  return {
    customizationsMap,
    isLoading: queries.some(q => q.isLoading),
    isError: queries.some(q => q.isError),
    errors: queries.filter(q => q.error).map(q => q.error),
    // All queries successful
    isSuccess: queries.every(q => q.isSuccess),
    // At least one query successful
    hasData: queries.some(q => q.data),
  }
}

/**
 * Prefetch customizations for menu items
 * Useful for preloading data before user interaction
 */
export const prefetchMenuItemCustomizations = async (
  queryClient: any,
  menuItemIds: ObjectId[]
) => {
  const prefetchPromises = menuItemIds.map((menuItemId) =>
    queryClient.prefetchQuery({
      queryKey: ['menu-customizations', menuItemId],
      queryFn: async () => {
        const response = await apiClient.menuItems.getMenuCustomisations(menuItemId)
        return response.success ? response.data || [] : []
      },
      staleTime: 15 * 60 * 1000,
    })
  )

  await Promise.allSettled(prefetchPromises)
}
